import {
  AuthenticationMethod,
  BasicTrackingEvent,
  CognitoAuthProvider,
  ContentType,
  Currency,
  CustomAuthProvider,
  EcommerceEvent,
  GoogleAnalyticsGroup,
  GoogleTagType,
} from '../constants';
import { getSource } from '../helpers/device';
import yieldToMain from '../helpers/yieldToMain';

interface ViewMapSourceParams {
  area: string;
  guestCount: string;
  roomCount: number;
  checkInDate?: string;
  checkOutDate?: string;
}

export default {
  facebook: {
    track: {
      search: async (parameter: Analytics.Facebook.Tracking.Parameter) => {
        await yieldToMain();
        if (typeof fbq !== 'undefined') {
          fbq('track', 'Search', parameter);
        }
      },
      view: async (parameter: Analytics.Facebook.Tracking.Parameter) => {
        await yieldToMain();
        if (typeof fbq !== 'undefined') {
          fbq('track', 'ViewContent', parameter);
        }
      },
      checkout: async (parameter: Analytics.Facebook.Tracking.Parameter) => {
        await yieldToMain();
        if (typeof fbq !== 'undefined') {
          fbq('track', 'InitiateCheckout', parameter);
        }
      },
      purchase: async (parameter: Analytics.Facebook.Tracking.Purchase) => {
        await yieldToMain();
        if (typeof fbq !== 'undefined') {
          fbq('track', 'Purchase', parameter);
        }
      },
    },
  },
  forcia: {
    track: {
      purchase: (purchase: Analytics.Forcia.Tracking.Purchase) => {
        // eslint-disable-next-line no-underscore-dangle
        window._f_data = purchase;
      },
    },
  },
  google: {
    basic: {
      track: {
        login: async (
          method:
            | AuthenticationMethod
            | CognitoAuthProvider
            | CustomAuthProvider = AuthenticationMethod.COGNITO,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.LOGIN, {
              send_to: groups,
              method,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        search: async (
          keyword: string,
          checkInDate: string,
          checkOutDate: string,
          roomCount: number,
          guestCount: string,
          type: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.SEARCH, {
              send_to: groups,
              search_term: keyword,
              check_in_date: checkInDate,
              check_out_date: checkOutDate,
              guest_count: guestCount,
              room_count: roomCount,
              type,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        select: async (
          type: ContentType,
          id: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.SELECT_CONTENT, {
              content_type: type,
              item_id: id,
              send_to: groups,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        signup: async (
          method: AuthenticationMethod = AuthenticationMethod.COGNITO,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.SIGN_UP, {
              send_to: groups,
              method,
              source: getSource(),
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        user: async (id: string) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            gtag(GoogleTagType.Set, 'user_id', id);
          }
        },
        locale: async (locale: string) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            gtag(GoogleTagType.Set, 'locale', locale);
          }
        },
        affiliate: async (id: string) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            gtag(GoogleTagType.Set, 'affiliate_id', id);
          }
        },
        tagFilter: async (
          tags: string[],
          keyword: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.TAG_FILTER, {
              send_to: groups,
              tags: tags.join(','),
              search_term: keyword,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        areaFilter: async (
          prefectureAreas: string[],
          keyword: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.AREA_FILTER, {
              send_to: groups,
              prefectureAreas: prefectureAreas.join(','),
              search_term: keyword,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        budgetFilter: async (
          budget: { min?: string; max?: string },
          keyword: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.BUDGET_FILTER, {
              send_to: groups,
              min: budget.min,
              max: budget.max,
              search_term: keyword,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        clearFilter: async (
          keyword: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, BasicTrackingEvent.CLEAR_FILTER, {
              send_to: groups,
              search_term: keyword,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
      },
    },
    ecommerce: {
      track: {
        cart: {
          add: async (
            items: Analytics.Google.Ecommerce.Tracking.Item[],
            value: number,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.ADD_TO_CART, {
                currency: Currency.JPY,
                value,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
          remove: async (
            items: Analytics.Google.Ecommerce.Tracking.Item[],
            value: number,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.REMOVE_FROM_CART, {
                currency: Currency.JPY,
                value,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
        },
        checkout: async (
          items: Analytics.Google.Ecommerce.Tracking.Item[],
          value: number,
          coupon?: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, EcommerceEvent.BEGIN_CHECKOUT, {
              currency: Currency.JPY,
              value,
              coupon,
              items,
              send_to: groups,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        item: {
          select: async (
            items: Analytics.Google.Ecommerce.Tracking.Item[],
            id?: string,
            name?: string,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.SELECT_ITEM, {
                item_list_id: id,
                item_list_name: name,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
          detail: async (
            items: Analytics.Google.Ecommerce.Tracking.Item[],
            value: number,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.VIEW_ITEM, {
                currency: Currency.JPY,
                value,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
          list: async (
            items: Analytics.Google.Ecommerce.Tracking.Item[],
            id?: string,
            name?: string,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.VIEW_ITEM_LIST, {
                item_list_id: id,
                item_list_name: name,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
        },
        payment: {
          add: async (
            currency: Currency,
            value: number,
            method: string,
            coupon?: string,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.ADD_PAYMENT_INFO, {
                currency,
                value,
                payment_type: method,
                coupon,
                items: [],
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
        },
        promotion: {
          click: async (
            promotion: Analytics.Google.Ecommerce.Tracking.Promotion,
            items?: Analytics.Google.Ecommerce.Tracking.Item[],
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.SELECT_PROMOTION, {
                ...promotion,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
          view: async (
            promotion: Analytics.Google.Ecommerce.Tracking.Promotion,
            items: Analytics.Google.Ecommerce.Tracking.Item[],
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.VIEW_PROMOTION, {
                ...promotion,
                items,
                send_to: groups,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
        },
        purchase: async (
          purchase: Analytics.Google.Ecommerce.Tracking.Purchase,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, EcommerceEvent.PURCHASE, {
              send_to: groups,
              ...purchase,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        refund: async (
          refund: Analytics.Google.Ecommerce.Tracking.Refund,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, EcommerceEvent.REFUND, {
              send_to: groups,
              ...refund,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        viewGallery: async (
          propertyId?: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, EcommerceEvent.VIEW_GALLERY, {
              send_to: groups,
              property_id: propertyId,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        viewFaq: async (
          itemId: string,
          question: string,
          faqId: number,
          propertyId?: string,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ) => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            const url = new URL(window?.location?.href);
            const params = new URLSearchParams(url.search);

            gtag(GoogleTagType.Event, EcommerceEvent.VIEW_FAQ, {
              property_id: propertyId,
              item_id: itemId,
              faq: question,
              faq_id: faqId,
              send_to: groups,
              affiliate_id: params.get('affiliate_id'),
            });
          }
        },
        map: {
          view: async (
            area: string,
            guestCount: string,
            roomCount: number,
            checkInDate?: string,
            checkOutDate?: string,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.VIEW_MAP, {
                send_to: groups,
                area,
                guestCount,
                roomCount,
                checkInDate,
                checkOutDate,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
          detail: {
            view: async (
              hotelId: string,
              guestCount: string,
              roomCount: number,
              checkInDate?: string,
              checkOutDate?: string,
              groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
            ) => {
              await yieldToMain();
              if (typeof gtag !== 'undefined') {
                const url = new URL(window?.location?.href);
                const params = new URLSearchParams(url.search);

                gtag(GoogleTagType.Event, EcommerceEvent.VIEW_MAP_SELECT_HOTEL, {
                  send_to: groups,
                  hotelId,
                  guestCount,
                  roomCount,
                  checkInDate,
                  checkOutDate,
                  affiliate_id: params.get('affiliate_id'),
                });
              }
            },
          },
          viewFromListingPage: async (
            { area, guestCount, roomCount, checkInDate, checkOutDate }: ViewMapSourceParams,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.VIEW_MAP_HOTEL_LISTING_PAGE, {
                send_to: groups,
                area,
                guestCount,
                roomCount,
                checkInDate,
                checkOutDate,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
          viewFromTopPage: async (
            { area, guestCount, roomCount, checkInDate, checkOutDate }: ViewMapSourceParams,
            groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
          ) => {
            await yieldToMain();
            if (typeof gtag !== 'undefined') {
              const url = new URL(window?.location?.href);
              const params = new URLSearchParams(url.search);

              gtag(GoogleTagType.Event, EcommerceEvent.VIEW_MAP_TOP_PAGE, {
                send_to: groups,
                area,
                guestCount,
                roomCount,
                checkInDate,
                checkOutDate,
                affiliate_id: params.get('affiliate_id'),
              });
            }
          },
        },
      },
    },
    performance: {
      webVital: {
        track: async (
          name: string,
          parameters: Record<string, unknown>,
          groups: GoogleAnalyticsGroup | GoogleAnalyticsGroup[] = GoogleAnalyticsGroup.TABIST
        ): Promise<void> => {
          await yieldToMain();
          if (typeof gtag !== 'undefined') {
            gtag(GoogleTagType.Event, name, {
              ...parameters,
              non_interaction: true,
              send_to: groups,
            });
          }
        },
      },
    },
  },
};
